import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Calculator, TrendingUp, AlertTriangle, FileText, Building2, 
  ChevronRight, Loader2, PiggyBank, Scale, CheckCircle2, Clock,
  ArrowRight, Info, Download, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPES_ACTIVITE = [
  { value: 'BIC_vente', label: 'Achat/Revente de marchandises', taux: '12.8%' },
  { value: 'BIC_service', label: 'Prestations de services (BIC)', taux: '22%' },
  { value: 'BNC', label: 'Professions libérales (BNC)', taux: '22%' },
  { value: 'BNC_cipav', label: 'Professions libérales CIPAV', taux: '22.6%' }
];

const CATEGORIES = [
  { value: 'commerce', label: 'Commerce' },
  { value: 'artisan', label: 'Artisanat' },
  { value: 'liberal', label: 'Profession libérale' }
];

// Composant Trimestre Card
function TrimestreCard({ trimestre, formatMontant }) {
  return (
    <div className="p-4 border rounded-lg">
      <p className="text-sm font-medium text-slate-500">T{trimestre.trimestre} 2026</p>
      <p className="text-lg font-bold mt-1">{formatMontant(trimestre.ca)}</p>
      <p className="text-sm text-red-600 mt-1">Cotisations: {formatMontant(trimestre.cotisations)}</p>
    </div>
  );
}

// Composant Comparaison Card
function ComparaisonCard({ title, subtitle, icon, bgColor, borderColor, data, formatMontant }) {
  var Icon = icon;
  var items = data.items || [];
  var total = data.total || 0;
  var totalLabel = data.totalLabel || 'Total';
  var totalColor = data.totalColor || 'text-slate-900';
  
  return (
    <Card className={borderColor ? 'border-2 ' + borderColor : ''}>
      <CardHeader className={bgColor}>
        <CardTitle className="flex items-center gap-2">
          <Icon className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        {items.map(function(item, idx) {
          return (
            <div key={idx} className="flex justify-between">
              <span className="text-slate-600">{item.label}</span>
              <span className={'font-mono text-sm ' + (item.negative ? 'text-red-600' : '')}>
                {item.negative ? '- ' : ''}{formatMontant(item.value)}
              </span>
            </div>
          );
        })}
        <div className="border-t pt-2">
          <div className="flex justify-between">
            <span className="font-bold">{totalLabel}</span>
            <span className={'font-mono font-bold ' + totalColor}>
              {formatMontant(total)}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportingFiscalPage() {
  const { token } = useAuth();
  const { formatMontant } = useSettings();
  const [loading, setLoading] = useState(true);
  const [simulation, setSimulation] = useState(null);
  const [comparaison, setComparaison] = useState(null);
  const [declarationForm, setDeclarationForm] = useState(null);
  const [selectedTrimestre, setSelectedTrimestre] = useState(1);
  const [settings, setSettings] = useState({
    type_activite: 'BIC_service',
    categorie: 'commerce',
    acre: false,
    versement_liberatoire: false
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('simulation');

  var fetchSimulation = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/fiscal/simulation', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setSimulation(response.data);
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching simulation:', error);
    }
  }, [token]);

  var fetchComparaison = useCallback(async function() {
    try {
      var response = await axios.get(API_URL + '/api/fiscal/simulation-societe', {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setComparaison(response.data);
    } catch (error) {
      console.error('Error fetching comparaison:', error);
    }
  }, [token]);

  var fetchDeclarationForm = useCallback(async function(trimestre) {
    try {
      var response = await axios.get(API_URL + '/api/fiscal/declaration-form/' + trimestre, {
        headers: { Authorization: 'Bearer ' + token },
        params: { annee: 2026 }
      });
      setDeclarationForm(response.data);
    } catch (error) {
      console.error('Error fetching declaration form:', error);
    }
  }, [token]);

  useEffect(function() {
    async function loadData() {
      setLoading(true);
      await Promise.all([fetchSimulation(), fetchComparaison()]);
      setLoading(false);
    }
    loadData();
  }, [fetchSimulation, fetchComparaison]);

  useEffect(function() {
    if (activeTab === 'declaration') {
      fetchDeclarationForm(selectedTrimestre);
    }
  }, [activeTab, selectedTrimestre, fetchDeclarationForm]);

  async function saveSettings() {
    setSaving(true);
    try {
      await axios.put(API_URL + '/api/fiscal/settings', settings, {
        headers: { Authorization: 'Bearer ' + token }
      });
      toast.success('Paramètres enregistrés');
      fetchSimulation();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  function handleSettingChange(key, value) {
    setSettings(function(prev) {
      var updated = {};
      for (var k in prev) {
        updated[k] = prev[k];
      }
      updated[key] = value;
      return updated;
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Extract data safely
  var caAnnuel = simulation ? simulation.ca_annuel : 0;
  var totalDepenses = simulation ? simulation.total_depenses : 0;
  var sim = simulation ? simulation.simulation : {};
  var seuilMicro = simulation ? simulation.seuil_micro : {};
  var trimestres = simulation ? simulation.trimestres : [];
  
  var comp = comparaison ? comparaison.comparaison : {};
  var micro = comp.micro_entreprise || {};
  var eurl = comp.eurl_is || {};
  var sasu = comp.sasu || {};
  var recommandation = comparaison ? comparaison.recommandation : null;

  // Render trimestre cards
  var trimestreCards = [];
  for (var i = 0; i < trimestres.length; i++) {
    trimestreCards.push(
      <TrimestreCard key={i} trimestre={trimestres[i]} formatMontant={formatMontant} />
    );
  }

  return (
    <div className="space-y-6" data-testid="reporting-fiscal-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Reporting Fiscal</h1>
          <p className="text-slate-500 mt-1">Simulation des charges et aide à la déclaration URSSAF</p>
        </div>
        <Button onClick={function() { fetchSimulation(); fetchComparaison(); }} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Alert seuil */}
      {seuilMicro.alerte && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-center gap-4">
            <AlertTriangle className="h-8 w-8 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-800">Attention au seuil micro-entreprise !</p>
              <p className="text-amber-700 text-sm">
                Vous avez atteint {seuilMicro.pourcentage}% du plafond. 
                Il vous reste {formatMontant(seuilMicro.reste_disponible)} avant le dépassement.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="simulation">
            <Calculator className="h-4 w-4 mr-2" />
            Simulation
          </TabsTrigger>
          <TabsTrigger value="comparaison">
            <Scale className="h-4 w-4 mr-2" />
            Comparaison
          </TabsTrigger>
          <TabsTrigger value="declaration">
            <FileText className="h-4 w-4 mr-2" />
            Déclaration
          </TabsTrigger>
          <TabsTrigger value="parametres">
            <Building2 className="h-4 w-4 mr-2" />
            Paramètres
          </TabsTrigger>
        </TabsList>

        {/* TAB: Simulation */}
        <TabsContent value="simulation" className="space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">CA Annuel</p>
                    <p className="text-xl font-bold text-slate-900">{formatMontant(caAnnuel)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <PiggyBank className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Cotisations URSSAF</p>
                    <p className="text-xl font-bold text-red-600">{formatMontant(sim.cotisations_urssaf || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <Calculator className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Total Charges</p>
                    <p className="text-xl font-bold text-amber-600">{formatMontant(sim.total_charges || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Revenu Net Estimé</p>
                    <p className="text-xl font-bold text-emerald-600">{formatMontant(sim.revenu_net || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Détail simulation */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Détail des charges</CardTitle>
                <CardDescription>Répartition des cotisations et taxes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">Cotisations URSSAF</p>
                    <p className="text-xs text-slate-500">Taux: {((sim.taux_urssaf || 0) * 100).toFixed(1)}%</p>
                  </div>
                  <p className="text-lg font-bold text-red-600">{formatMontant(sim.cotisations_urssaf || 0)}</p>
                </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">CFP (Formation)</p>
                    <p className="text-xs text-slate-500">Contribution Formation Professionnelle</p>
                  </div>
                  <p className="text-lg font-bold text-slate-700">{formatMontant(sim.cfp || 0)}</p>
                </div>

                {(sim.versement_liberatoire || 0) > 0 && (
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium">Versement libératoire IR</p>
                      <p className="text-xs text-slate-500">Impôt sur le revenu</p>
                    </div>
                    <p className="text-lg font-bold text-slate-700">{formatMontant(sim.versement_liberatoire || 0)}</p>
                  </div>
                )}

                <div className="border-t pt-3">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-lg">TOTAL CHARGES</p>
                    <p className="text-xl font-bold text-red-600">{formatMontant(sim.total_charges || 0)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Seuil Micro-Entreprise</CardTitle>
                <CardDescription>Plafond de chiffre d'affaires autorisé</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progression vers le plafond</span>
                    <span className={seuilMicro.alerte ? 'text-amber-600 font-bold' : ''}>
                      {seuilMicro.pourcentage || 0}%
                    </span>
                  </div>
                  <Progress 
                    value={seuilMicro.pourcentage || 0} 
                    className={seuilMicro.alerte ? 'bg-amber-100' : ''}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="p-3 bg-blue-50 rounded-lg text-center">
                    <p className="text-xs text-blue-600">Plafond</p>
                    <p className="text-lg font-bold text-blue-700">{formatMontant(seuilMicro.seuil || 0)}</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-xs text-emerald-600">Disponible</p>
                    <p className="text-lg font-bold text-emerald-700">{formatMontant(seuilMicro.reste_disponible || 0)}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
                  <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <p>
                    Si vous dépassez le seuil 2 années consécutives, vous devrez changer de régime fiscal.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cotisations par trimestre */}
          <Card>
            <CardHeader>
              <CardTitle>Cotisations par trimestre</CardTitle>
              <CardDescription>Estimation des cotisations à payer par période</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {trimestreCards}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Comparaison */}
        <TabsContent value="comparaison" className="space-y-6">
          {recommandation && (
            <Card className="border-blue-300 bg-blue-50">
              <CardContent className="p-4 flex items-center gap-4">
                <CheckCircle2 className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-semibold text-blue-800">
                    Recommandation : {(recommandation.statut || '').replace('_', ' ').toUpperCase()}
                  </p>
                  <p className="text-blue-700 text-sm">{recommandation.raison}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ComparaisonCard
              title="Micro-Entreprise"
              subtitle="Votre statut actuel"
              icon={Building2}
              bgColor="bg-blue-50"
              borderColor="border-blue-200"
              formatMontant={formatMontant}
              data={{
                items: [
                  { label: 'Cotisations', value: micro.cotisations || 0, negative: true },
                  { label: 'IR estimé', value: micro.ir_estime || 0, negative: true }
                ],
                total: micro.net_apres_ir || 0,
                totalLabel: 'Net après IR',
                totalColor: 'text-emerald-600'
              }}
            />

            <ComparaisonCard
              title="EURL à l'IS"
              subtitle="Société unipersonnelle"
              icon={Scale}
              bgColor="bg-purple-50"
              formatMontant={formatMontant}
              data={{
                items: [
                  { label: 'Rémunération gérant', value: eurl.remuneration_gerant || 0 },
                  { label: 'Cotisations TNS', value: eurl.cotisations_tns || 0, negative: true },
                  { label: 'IS', value: eurl.is || 0, negative: true },
                  { label: 'Dividendes nets', value: eurl.dividendes_nets || 0 }
                ],
                total: eurl.revenu_total || 0,
                totalLabel: 'Revenu total',
                totalColor: 'text-purple-600'
              }}
            />

            <ComparaisonCard
              title="SASU"
              subtitle="Société par actions"
              icon={Building2}
              bgColor="bg-amber-50"
              formatMontant={formatMontant}
              data={{
                items: [
                  { label: 'Salaire net', value: sasu.salaire_net || 0 },
                  { label: 'Charges patronales', value: sasu.charges_patronales || 0, negative: true },
                  { label: 'IS', value: sasu.is || 0, negative: true },
                  { label: 'Dividendes nets', value: sasu.dividendes_nets || 0 }
                ],
                total: sasu.revenu_total || 0,
                totalLabel: 'Revenu total',
                totalColor: 'text-amber-600'
              }}
            />
          </div>
        </TabsContent>

        {/* TAB: Déclaration */}
        <TabsContent value="declaration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Formulaire de déclaration pré-rempli</CardTitle>
              <CardDescription>Sélectionnez le trimestre pour générer votre déclaration</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <Label>Trimestre</Label>
                <Select value={String(selectedTrimestre)} onValueChange={function(v) { setSelectedTrimestre(parseInt(v)); }}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">T1 - Janvier à Mars</SelectItem>
                    <SelectItem value="2">T2 - Avril à Juin</SelectItem>
                    <SelectItem value="3">T3 - Juillet à Septembre</SelectItem>
                    <SelectItem value="4">T4 - Octobre à Décembre</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {declarationForm && (
                <DeclarationFormDisplay form={declarationForm} formatMontant={formatMontant} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Paramètres */}
        <TabsContent value="parametres" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Paramètres fiscaux</CardTitle>
              <CardDescription>Configurez votre situation pour des simulations précises</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>Type d'activité</Label>
                <Select value={settings.type_activite} onValueChange={function(v) { handleSettingChange('type_activite', v); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TYPES_ACTIVITE.map(function(type) {
                      return (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label} ({type.taux})
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={settings.categorie} onValueChange={function(v) { handleSettingChange('categorie', v); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(function(cat) {
                      return (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">ACRE</p>
                  <p className="text-sm text-slate-500">Aide à la Création/Reprise d'Entreprise (50% de réduction la 1ère année)</p>
                </div>
                <Switch
                  checked={settings.acre}
                  onCheckedChange={function(v) { handleSettingChange('acre', v); }}
                />
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Versement libératoire de l'IR</p>
                  <p className="text-sm text-slate-500">Payer l'impôt sur le revenu en même temps que les cotisations</p>
                </div>
                <Switch
                  checked={settings.versement_liberatoire}
                  onCheckedChange={function(v) { handleSettingChange('versement_liberatoire', v); }}
                />
              </div>

              <Button onClick={saveSettings} disabled={saving} className="w-full">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Enregistrer les paramètres
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Composant Declaration Form Display
function DeclarationFormDisplay({ form, formatMontant }) {
  var montants = form.montants || {};
  var instructions = form.instructions || [];
  
  var instructionItems = [];
  for (var i = 0; i < instructions.length; i++) {
    instructionItems.push(
      <li key={i} className="flex items-start gap-3">
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
          {i + 1}
        </span>
        <span className="text-slate-700">{instructions[i]}</span>
      </li>
    );
  }

  return (
    <div className="mt-6 space-y-4">
      <div className="p-4 bg-blue-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="font-bold text-blue-800">Période: {form.periode}</span>
          <span className="flex items-center gap-1 text-amber-600">
            <Clock className="h-4 w-4" />
            Date limite: {form.date_limite}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 border rounded-lg text-center">
          <p className="text-sm text-slate-500">CA du trimestre</p>
          <p className="text-2xl font-bold text-blue-600">{formatMontant(montants.ca_trimestre || 0)}</p>
        </div>
        <div className="p-4 border rounded-lg text-center">
          <p className="text-sm text-slate-500">Taux de cotisations</p>
          <p className="text-2xl font-bold text-slate-700">{((montants.taux_cotisations || 0) * 100).toFixed(1)}%</p>
        </div>
        <div className="p-4 border rounded-lg text-center bg-red-50">
          <p className="text-sm text-red-600">Cotisations à payer</p>
          <p className="text-2xl font-bold text-red-600">{formatMontant(montants.cotisations_dues || 0)}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Instructions de déclaration</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-2">
            {instructionItems}
          </ol>
          <Button className="mt-4 w-full" onClick={function() { window.open('https://www.autoentrepreneur.urssaf.fr', '_blank'); }}>
            <ArrowRight className="h-4 w-4 mr-2" />
            Accéder à autoentrepreneur.urssaf.fr
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default ReportingFiscalPage;
